import React from 'react';
import { logger } from '@/utils/logger';

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * 
 * Specifically designed to handle:
 * - Field mapping transformation errors
 * - Service layer API errors
 * - Component rendering errors
 * - Redux state inconsistencies
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      is_network_error: false,
      is_checking_connection: false
    };
  }

  static getDerivedStateFromError(error) {
    // Detect network/lazy loading errors
    const is_network_error = ErrorBoundary.isNetworkError(error);

    return {
      hasError: true,
      is_network_error,
      error // Store error in state immediately for custom fallbacks
    };
  }

  /**
   * Detects if an error is related to network/lazy loading issues
   * @param {Error} error - The error to check
   * @returns {boolean} - True if it's a network error
   */
  static isNetworkError(error) {
    if (!error || !error.message) return false;

    const network_error_patterns = [
      'Failed to fetch dynamically imported module',
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_NETWORK_CHANGED',
      'Load failed',
      'Loading chunk'
    ];

    return network_error_patterns.some(pattern =>
      error.message.includes(pattern)
    );
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    logger.error('ErrorBoundary', 'React component crashed', {
      error: error.message,
      component_stack: errorInfo.componentStack,
      location: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });

    // Store error details in state for display
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log specific error types
    if (this.state.is_network_error) {
      logger.error('ErrorBoundary', 'Network/lazy loading error detected', {
        error: error.message,
      });
    } else if (error.message && error.message.includes('field')) {
      logger.error('ErrorBoundary', 'Field mapping error detected', {
        error: error.message,
      });
    } else if (error.message && (error.message.includes('service') || error.message.includes('API'))) {
      logger.error('ErrorBoundary', 'Service layer error detected', {
        error: error.message,
      });
    }

    // Report to error monitoring service in production
    if (import.meta.env.PROD && this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Checks if the device has internet connection
   * @returns {Promise<boolean>} - True if online
   */
  checkConnection = async () => {
    this.setState({ is_checking_connection: true });

    try {
      // Try to fetch a lightweight resource (favicon.ico pesa ~0.6 KB)
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors'
      });

      this.setState({ is_checking_connection: false });
      return true;
    } catch (error) {
      this.setState({ is_checking_connection: false });
      return false;
    }
  };

  handleRetry = async () => {
    // If it's a network error, check connection first
    if (this.state.is_network_error) {
      const is_online = await this.checkConnection();

      if (!is_online) {
        alert('Aún no tienes conexión a internet. Por favor, verifica tu conexión y vuelve a intentar.');
        return;
      }
    }

    // Reset error state and retry
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      is_network_error: false
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Network error specific UI
      if (this.state.is_network_error) {
        return (
          <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
              <div className="bg-raios-secondary/90 backdrop-blur-xl border border-white/10 rounded-lg p-8 text-center">
                <div className="mx-auto h-16 w-16 text-raios-text-support mb-6">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
                  </svg>
                </div>

                <h2 className="text-3xl font-bold font-mono text-raios-text-high mb-3">
                  Sin conexión a internet
                </h2>

                <p className="text-raios-text-support mb-6">
                  No se pudo cargar la página porque no hay conexión a internet. Por favor, verifica tu conexión y vuelve a intentar.
                </p>

                <div className="bg-white/10 border border-white/15 rounded-md p-4 mb-6">
                  <div className="flex items-start text-left">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="h-5 w-5 text-raios-text-support" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-semibold text-raios-text-high mb-2">
                        Consejos para resolver el problema:
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-raios-text-support">
                        <li>Verifica que tu WiFi o datos móviles estén activados</li>
                        <li>Intenta abrir otra página web para confirmar la conexión</li>
                        <li>Reinicia tu router si estás usando WiFi</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-3">
                  <button
                    onClick={this.handleRetry}
                    disabled={this.state.is_checking_connection}
                    className="btn btn-lg w-full bg-raios-primary text-raios-text-high hover:bg-raios-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {this.state.is_checking_connection ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-raios-text-high" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verificando conexión...
                      </>
                    ) : (
                      'Verificar conexión y reintentar'
                    )}
                  </button>

                  <button
                    onClick={() => window.location.href = '/'}
                    className="btn btn-ghost btn-lg w-full opacity-90 hover:opacity-100"
                  >
                    Ir al inicio
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Default fallback UI for non-network errors
      return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="bg-raios-secondary/90 backdrop-blur-xl border border-white/10 rounded-lg p-8 text-center">
              <div className="mx-auto h-12 w-12 text-red-400 mb-6">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>

              <h2 className="text-3xl font-bold font-mono text-raios-text-high mb-3">
                Algo salió mal
              </h2>

              <p className="text-raios-text-support mb-6">
                Ha ocurrido un error inesperado. Por favor, intenta de nuevo.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-md text-left">
                  <summary className="cursor-pointer font-semibold text-raios-text-high">
                    Detalles del error (modo desarrollo)
                  </summary>
                  <div className="mt-2 text-sm text-raios-text-support">
                    <p className="font-semibold text-raios-text-high">Error:</p>
                    <p className="mb-2">{this.state.error.message}</p>

                    <p className="font-semibold text-raios-text-high">Stack:</p>
                    <pre className="whitespace-pre-wrap text-xs bg-black/40 p-2 rounded mt-1">
                      {this.state.error.stack}
                    </pre>

                    {this.state.errorInfo && (
                      <>
                        <p className="font-semibold text-raios-text-high mt-2">Component Stack:</p>
                        <pre className="whitespace-pre-wrap text-xs bg-black/40 p-2 rounded mt-1">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}

              <div className="mt-6 flex flex-col space-y-3">
                <button
                  onClick={this.handleRetry}
                  className="btn btn-lg w-full bg-raios-primary text-raios-text-high hover:bg-raios-primary/90"
                >
                  Intentar de nuevo
                </button>

                <button
                  onClick={() => window.location.href = '/'}
                  className="btn btn-ghost btn-lg w-full opacity-90 hover:opacity-100"
                >
                  Ir al inicio
                </button>
              </div>

              <p className="mt-4 text-xs text-raios-text-support">
                Si el problema persiste, puedes{' '}
                <a href="mailto:soporte@marketplace.com" className="text-raios-primary hover:text-raios-tertiary underline">
                  contactar soporte
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Service Error Boundary
 * 
 * Specialized error boundary for service layer errors
 * with specific handling for field mapping and API errors
 */
export class ServiceErrorBoundary extends ErrorBoundary {
  componentDidCatch(error, errorInfo) {
    // Call parent method
    super.componentDidCatch(error, errorInfo);

    // Additional logging for service errors
    logger.error('ServiceErrorBoundary', 'Service layer error', {
      error: error.message,
      component: errorInfo.componentStack.split('\n')[1],
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md bg-red-900/20 border border-red-500/30 p-4 m-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-300">
                Error de conexión
              </h3>
              <div className="mt-2 text-sm text-red-400">
                <p>No se pudo cargar la información. Verifica tu conexión a internet.</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    onClick={this.handleRetry}
                    className="px-2 py-1.5 rounded-md text-sm font-medium text-red-300 hover:bg-red-900/40 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Form Error Boundary  
 * 
 * Specialized error boundary for form components
 * with specific handling for field mapping and validation errors
 */
export class FormErrorBoundary extends ErrorBoundary {
  componentDidCatch(error, errorInfo) {
    // Call parent method
    super.componentDidCatch(error, errorInfo);

    // Additional logging for form errors
    logger.error('FormErrorBoundary', 'Form error', {
      error: error.message,
      component: errorInfo.componentStack.split('\n')[1],
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md bg-yellow-900/20 border border-yellow-500/30 p-4 m-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-300">
                Error en el formulario
              </h3>
              <div className="mt-2 text-sm text-yellow-400">
                <p>Ha ocurrido un problema con el formulario. Por favor, recarga la página.</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    onClick={this.handleRetry}
                    className="px-2 py-1.5 rounded-md text-sm font-medium text-yellow-300 hover:bg-yellow-900/40 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher Order Component for Error Boundary
 * 
 * Wraps any component with error boundary functionality
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;